// Perfis de acesso por empresa (docs/PERMISSOES.md secao 11). Padrao = os 11 papeis (somente leitura); proprio = copia editavel.
import { z } from 'zod';
import { cuid } from './primitives.js';
import { PAPEIS_BASE_DE_PERFIL, PERMISSOES, PERMISSOES_RESERVADAS, type Papel, type Permissao } from './permissoes.js';

/** Permissoes que o perfil proprio pode marcar (as reservadas ficam fora). */
export const PERMISSOES_DE_PERFIL: readonly Permissao[] = PERMISSOES.filter((p) => !PERMISSOES_RESERVADAS.includes(p));

/** Destacadas na tela: custo, salario/pessoal, margem e lucro em R$, RH. */
export const PERMISSOES_SENSIVEIS: readonly Permissao[] = ['custo.ver', 'pessoal.ver', 'lucro.ver', 'margens.ver', 'rh.ver', 'rh.editar', 'custoAquisicao.editar', 'cpm.ver', 'cpm.editar'];

export interface GrupoPermissoes {
  chave: string;
  titulo: string;
  permissoes: Permissao[];
}

/** Agrupamento por modulo para a tela. Toda permissao do catalogo (exceto reservadas) aparece em exatamente um grupo. */
export const GRUPOS_DE_PERMISSOES: readonly GrupoPermissoes[] = [
  { chave: 'precos', titulo: 'Preços e margens', permissoes: ['preco.ver', 'lucro.ver', 'margens.ver', 'precificacao.simular', 'precificacao.gravar', 'lista.ver', 'praticado.gravar'] },
  { chave: 'vendas', titulo: 'Orçamentos, clientes e carteira', permissoes: ['orcamento.ver', 'orcamento.verTodos', 'orcamento.editar', 'cliente.ver', 'cliente.editar', 'carteira.ver', 'carteira.verTodos', 'carteira.editar'] },
  { chave: 'custos', titulo: 'Custos', permissoes: ['custo.ver', 'custoAquisicao.editar', 'ficha.ver', 'ficha.editar', 'centro.ver', 'centro.editar', 'cpm.ver', 'cpm.editar', 'rateio.ver', 'rateio.editar', 'lote.ver', 'lote.decidir', 'marca.dispensar'] },
  { chave: 'cadastros', titulo: 'Cadastros', permissoes: ['item.ver', 'item.editar', 'tributo.editar', 'tabela.ver', 'tabela.editar'] },
  { chave: 'pessoas', titulo: 'Pessoas e RH', permissoes: ['pessoal.ver', 'rh.ver', 'rh.editar'] },
  { chave: 'plano', titulo: 'Plano de vendas', permissoes: ['plano.ver', 'plano.editar'] },
  { chave: 'empresa', titulo: 'Empresa e configurações', permissoes: ['empresa.ver', 'empresa.editar', 'parametros.ver', 'parametros.editar', 'onboarding.executar', 'auditoria.ver'] },
];

const permissaoEnum = z.enum(PERMISSOES_DE_PERFIL as [Permissao, ...Permissao[]]);
const nomePerfil = z.string().trim().min(2, 'Informe o nome (mínimo 2 caracteres)').max(60, 'O nome tem no máximo 60 caracteres');
const permissoesPerfil = z.array(permissaoEnum).max(PERMISSOES_DE_PERFIL.length).transform((l) => [...new Set(l)]);

/** POST /perfis: copia de um padrao (baseadoEm) ou de outro perfil proprio (copiarDe), com as permissoes marcadas. */
export const criarPerfilSchema = z.object({
  nome: nomePerfil,
  baseadoEm: z.enum(PAPEIS_BASE_DE_PERFIL as [Papel, ...Papel[]]),
  permissoes: permissoesPerfil,
});
export type CriarPerfilDto = z.infer<typeof criarPerfilSchema>;

/** PUT /perfis/:id */
export const editarPerfilSchema = z.object({ nome: nomePerfil, permissoes: permissoesPerfil });
export type EditarPerfilDto = z.infer<typeof editarPerfilSchema>;

/** POST /perfis/:id/atribuir */
export const atribuirPerfilSchema = z.object({ usuarioId: cuid, substituirPadrao: z.boolean().default(false) });
export type AtribuirPerfilDto = z.output<typeof atribuirPerfilSchema>;

export interface PerfilPadraoLinha {
  papel: Papel;
  permissoes: Permissao[];
  /** Pode servir de base para um perfil proprio. */
  base: boolean;
}

export interface PerfilUsuarioRef {
  usuarioId: string;
  nome: string;
  email: string;
}

export interface PerfilProprioLinha {
  id: string;
  nome: string;
  baseadoEm: Papel;
  permissoes: Permissao[];
  usuarios: PerfilUsuarioRef[];
  /** Permissoes do perfil que quem consulta nao tem (nao poderia conceder): a tela avisa. */
  acimaDoTeto: Permissao[];
}

export interface PerfisResponse {
  padroes: PerfilPadraoLinha[];
  proprios: PerfilProprioLinha[];
  /** Permissoes que quem consulta pode marcar (teto = as dele, menos as reservadas). */
  concediveis: Permissao[];
  grupos: GrupoPermissoes[];
  sensiveis: Permissao[];
  /** Usuarios da empresa que podem receber um perfil (acesso na empresa e dentro do teto de papel). */
  usuariosDaEmpresa: PerfilUsuarioRef[];
}
